import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { companyInfo } from "@/data/products";
import pureproLogo from "@/assets/purepro-logo.png";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";

const mainNavLinks = [
  { href: "/products", label: "Products" },
  { href: "/nihon-trim", label: "Nihon Trim" },
];

const resourceLinks = [
  { href: "/blog", label: "Blog" },
  { href: "/videos", label: "Videos" },
  { href: "/catalogues", label: "Catalogues" },
  { href: "/certifications", label: "Certifications" },
  { href: "/research", label: "Research" },
  { href: "/health-benefits", label: "Health Benefits" },
];

const companyLinks = [
  { href: "/about-us", label: "About Us" },
  { href: "/reviews", label: "Reviews" },
  { href: "/dealership-request-form", label: "Dealership" },
  { href: "/contact-us", label: "Contact Us" },
];

const allNavLinks = [...mainNavLinks, ...resourceLinks, ...companyLinks];

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? "bg-card/98 backdrop-blur-md border-b border-border shadow-sm" 
          : "bg-card/95 backdrop-blur-md border-b border-border"
      }`}
    >
      <div className="container-premium">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center group flex-shrink-0">
            <motion.img 
              src={pureproLogo} 
              alt="Pure Pro India - Drinking Water System" 
              className="h-10 md:h-12 w-auto object-contain"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            />
          </Link>

          {/* Desktop Navigation */}
          <NavigationMenu className="hidden lg:flex">
            <NavigationMenuList className="gap-1">
              {/* Main Links */}
              {mainNavLinks.map((link) => (
                <NavigationMenuItem key={link.href}>
                  <Link
                    to={link.href}
                    className={cn(
                      "inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors hover:bg-accent/10 hover:text-accent focus:bg-accent/10 focus:text-accent focus:outline-none",
                      location.pathname === link.href ? "text-accent" : "text-foreground"
                    )}
                  >
                    {link.label}
                  </Link>
                </NavigationMenuItem>
              ))}

              {/* Resources Dropdown */}
              <NavigationMenuItem className="relative">
                <NavigationMenuTrigger className="bg-transparent hover:bg-accent/10 hover:text-accent data-[state=open]:bg-accent/10">
                  Resources
                </NavigationMenuTrigger>
                <NavigationMenuContent className="absolute left-0 top-full mt-1 w-48 bg-card border border-border rounded-lg shadow-xl z-[100]">
                  <ul className="py-2">
                    {resourceLinks.map((link) => (
                      <li key={link.href}>
                        <NavigationMenuLink asChild>
                          <Link
                            to={link.href}
                            className={cn(
                              "block px-4 py-2.5 text-sm transition-colors hover:bg-secondary hover:text-accent",
                              location.pathname === link.href ? "text-accent bg-secondary/50" : "text-foreground"
                            )}
                          >
                            {link.label}
                          </Link>
                        </NavigationMenuLink>
                      </li>
                    ))}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>

              {/* Company Dropdown */}
              <NavigationMenuItem className="relative">
                <NavigationMenuTrigger className="bg-transparent hover:bg-accent/10 hover:text-accent data-[state=open]:bg-accent/10">
                  Company
                </NavigationMenuTrigger>
                <NavigationMenuContent className="absolute left-0 top-full mt-1 w-48 bg-card border border-border rounded-lg shadow-xl z-[100]">
                  <ul className="py-2">
                    {companyLinks.map((link) => (
                      <li key={link.href}>
                        <NavigationMenuLink asChild>
                          <Link
                            to={link.href}
                            className={cn(
                              "block px-4 py-2.5 text-sm transition-colors hover:bg-secondary hover:text-accent",
                              location.pathname === link.href ? "text-accent bg-secondary/50" : "text-foreground"
                            )}
                          >
                            {link.label}
                          </Link>
                        </NavigationMenuLink>
                      </li>
                    ))}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>

          {/* CTA Button & Mobile Menu */}
          <div className="flex items-center gap-4">
            <a href={`tel:${companyInfo.contact.phone}`} className="hidden md:flex items-center gap-2 text-sm font-medium text-primary hover:text-accent transition-colors">
              <Phone className="w-4 h-4" />
              <span>{companyInfo.contact.phone}</span>
            </a>
            <Button asChild className="hidden sm:flex btn-cyan border-0">
              <Link to="/contact-us">
                Get a Quote
              </Link>
            </Button>

            {/* Mobile Menu Toggle */}
            <motion.button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 text-primary hover:text-accent transition-colors"
              aria-label="Toggle menu"
              whileTap={{ scale: 0.9 }}
            >
              <AnimatePresence mode="wait">
                {isMenuOpen ? (
                  <motion.div
                    key="close"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <X className="w-6 h-6" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Menu className="w-6 h-6" />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="lg:hidden overflow-hidden"
            >
              <nav className="py-6 border-t border-border">
                <motion.div 
                  className="flex flex-col gap-2"
                  initial="closed"
                  animate="open"
                  variants={{
                    open: {
                      transition: { staggerChildren: 0.05 }
                    },
                    closed: {}
                  }}
                >
                  {allNavLinks.map((link) => (
                    <motion.div
                      key={link.href}
                      variants={{
                        open: { opacity: 1, x: 0 },
                        closed: { opacity: 0, x: -20 }
                      }}
                    >
                      <Link
                        to={link.href}
                        className={`block text-base font-medium py-3 px-4 rounded-lg transition-all duration-300 hover:bg-secondary ${
                          location.pathname === link.href 
                            ? "text-accent bg-accent/10" 
                            : "text-foreground hover:text-accent"
                        }`}
                      >
                        {link.label}
                      </Link>
                    </motion.div>
                  ))}
                  <motion.div
                    variants={{
                      open: { opacity: 1, x: 0 },
                      closed: { opacity: 0, x: -20 }
                    }}
                    className="pt-4 border-t border-border mt-2"
                  >
                    <a href={`tel:${companyInfo.contact.phone}`} className="flex items-center gap-2 text-sm font-medium text-primary mb-4 px-4">
                      <Phone className="w-4 h-4" />
                      <span>{companyInfo.contact.phone}</span>
                    </a>
                    <Button asChild className="w-full btn-cyan border-0">
                      <Link to="/contact-us">
                        Get a Quote
                      </Link>
                    </Button>
                  </motion.div>
                </motion.div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

export default Header;
